import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  UploadSimple, DownloadSimple, Trash, FileText, Eye, PencilSimple,
  ArrowsClockwise, Check, X,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { useMasterData } from "@/hooks/useMasterData";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const humanize = (v) => (v || "other").split(/[_-]/).map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" ");

export default function Documents({ candidateId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("resume");
  const [editing, setEditing] = useState(null); // { id, description }
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceFor, setReplaceFor] = useState(null); // doc being replaced
  const { items: docCategories } = useMasterData("document_category");
  const isLeader = user?.role === "admin" || user?.role === "team_lead";

  // Build the category list from Master Data (fallback to legacy static list if MD empty)
  const categoryOptions = docCategories.length
    ? docCategories.map((d) => ({ v: d.value.toLowerCase().replace(/\s+/g, "_"), l: d.value }))
    : [
        { v: "resume", l: "Resume" }, { v: "cover_letter", l: "Cover Letter" },
        { v: "evaluation_sheet", l: "Evaluation Sheet" }, { v: "evaluation_recording", l: "Evaluation Recording" },
        { v: "certificate", l: "Certificate" }, { v: "visa_document", l: "Visa Document" },
        { v: "portfolio", l: "Portfolio" }, { v: "offer_letter", l: "Offer Letter" },
        { v: "interview_screenshot", l: "Interview Screenshot" }, { v: "other", l: "Other" },
      ];

  const load = useCallback(
    () => api.get(`/documents?candidate_id=${candidateId}`).then((r) => setItems(r.data)),
    [candidateId]
  );
  useEffect(() => { load(); }, [load]);

  const upload = async (file, targetType, isReplace = false) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("candidate_id", candidateId);
      fd.append("doc_type", targetType);
      fd.append("file", file);
      const res = await fetch(`${BACKEND}/api/documents/upload`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(formatApiError(j.detail) || "Upload failed");
      }
      toast.success(isReplace ? "Replacement uploaded (new version)" : "Uploaded");
      load();
    } catch (ex) { toast.error(ex.message); }
    finally { setUploading(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    await upload(f, docType, false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onReplaceFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !replaceFor) return;
    await upload(f, replaceFor.doc_type, true);
    if (replaceRef.current) replaceRef.current.value = "";
    setReplaceFor(null);
  };

  const download = async (id, filename) => {
    try {
      const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || `document-${id}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const preview = (id) => {
    // preview uses cookie auth, so we can just open the URL in a new tab
    window.open(`${BACKEND}/api/documents/${id}/preview`, "_blank", "noreferrer");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document? (soft-delete, admins can recover from Mongo)")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const saveDescription = async () => {
    if (!editing) return;
    try {
      await api.patch(`/documents/${editing.id}`, { description: editing.description });
      setEditing(null); toast.success("Notes saved"); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="documents-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold">Documents</h3>
          <p className="text-xs text-slate-500">Categories from Master Data · versioned · previewed inline · admins can delete.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="doc-category-select"
                  className="border border-slate-200 rounded-md px-3 py-2 text-sm">
            {categoryOptions.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
          </select>
          <label className="inline-flex items-center gap-1 text-sm bg-sky-600 hover:bg-sky-700 text-white px-3 py-2 rounded-md cursor-pointer">
            <UploadSimple size={14} /> {uploading ? "Uploading…" : "Upload"}
            <input ref={fileRef} type="file" onChange={onFile} className="hidden" disabled={uploading} data-testid="doc-file-input" />
          </label>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {items.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No documents.</div>}
        {items.map((d) => {
          const isEditing = editing?.id === d.id;
          return (
            <div key={d.id} className="py-3 flex items-start gap-3 text-sm" data-testid={`doc-row-${d.id}`}>
              <FileText size={18} className="text-slate-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.original_filename}</div>
                <div className="text-xs text-slate-500">
                  {humanize(d.doc_type)} · v{d.version} · {(d.size / 1024).toFixed(1)} KB · uploaded by {d.uploaded_by_name} on {new Date(d.uploaded_at).toLocaleDateString()}
                </div>
                {isEditing ? (
                  <div className="flex items-center gap-2 mt-1">
                    <input value={editing.description || ""} autoFocus
                           onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))}
                           placeholder="Add notes / description…"
                           data-testid={`doc-desc-input-${d.id}`}
                           className="flex-1 border border-slate-200 rounded-md px-2 py-1 text-xs" />
                    <button onClick={saveDescription} className="text-emerald-700"><Check size={14} /></button>
                    <button onClick={() => setEditing(null)} className="text-slate-400"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic mt-0.5">
                    {d.description || <span className="text-slate-300">No notes</span>}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 pt-0.5">
                <button onClick={() => preview(d.id)} title="Preview" className="text-slate-500 hover:text-sky-700" data-testid={`doc-preview-${d.id}`}><Eye size={16} /></button>
                <button onClick={() => download(d.id, d.original_filename)} title="Download" className="text-slate-500 hover:text-sky-700"><DownloadSimple size={16} /></button>
                <button onClick={() => setEditing({ id: d.id, description: d.description || "" })} title="Edit notes" className="text-slate-400 hover:text-slate-700"><PencilSimple size={14} /></button>
                <label title="Replace (uploads new version)" className="cursor-pointer text-slate-400 hover:text-amber-700" data-testid={`doc-replace-${d.id}`}>
                  <ArrowsClockwise size={14} />
                  <input ref={d.id === replaceFor?.id ? replaceRef : null} type="file" className="hidden"
                         onClick={() => setReplaceFor(d)}
                         onChange={(e) => { setReplaceFor(d); onReplaceFile(e); }} />
                </label>
                {isLeader && (
                  <button onClick={() => remove(d.id)} title="Delete" className="text-slate-400 hover:text-red-600" data-testid={`doc-delete-${d.id}`}><Trash size={16} /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
